import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { combineLatest, filter, of, switchMap, take, withLatestFrom } from 'rxjs';
import { CustomDotCmsContentlet } from 'src/app/extensions/dot-cms/models/contentlet/custom-dotcms-contentlet.model';

import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { GTMHelper } from 'ish-core/models/gtm/gtm.helper';
import { DataLayerObject, GTMEventItems } from 'ish-core/models/gtm/gtm.model';
import { LineItem } from 'ish-core/models/line-item/line-item.model';
import { Order } from 'ish-core/models/order/order.model';
import { Payment } from 'ish-core/models/payment/payment.model';
import { getCurrentCurrency } from 'ish-core/store/core/configuration';
import { getBasketItem, getCurrentBasket } from 'ish-core/store/customer/basket/basket.selectors';
import { getLoggedInUser } from 'ish-core/store/customer/user/user.selectors';
import { CookiesService } from 'ish-core/utils/cookies/cookies.service';

@Injectable({ providedIn: 'root' })
export class GoogleTagManagerService {
  currentCurrency: string;
  constructor(
    private router: Router,
    private store: Store,
    private gtmFacade: GoogleTagManagerFacade,
    private cookiesService: CookiesService
  ) {
    this.store
      .pipe(select(getCurrentCurrency))
      .pipe(filter(currency => !!currency))
      .subscribe(currency => {
        this.currentCurrency = currency;
      });
  }
  //GTM - View-Promotion
  triggeringPromotionGTMEvent(event: string, items?: GTMEventItems[]) {
    this.gtmFacade.triggeringContentletEvent(event, items);
  }
  //GTM-Select-Promotion
  triggeringSelectPromotionGTMEvent(event: string, items: CustomDotCmsContentlet[]) {
    const promotionContent: {} = {
      sku: items[0]?.productSKU,
      creative_name: items[0]?.title || items[0]?.bannerTitle,
      creative_slot: items[0]?.contentType,
      promotion_name: items[0]?.name || items[0]?.caption,
      promotion_id: items[0]?.identifier,
    };
    this.triggeringListItemEvent(event, [promotionContent]);
    const link = items[0]?.link;
    if (link) {
      if (link.startsWith('http://') || link.startsWith('https://')) {
        window.location.href = link;
      } else {
        this.router.navigate([link]);
      }
    }
  }
  //Add to cart,view_item,view_item_list,remove_from_cart,review_checkout
  triggeringListItemEvent(event: string, eventItems?: {}[], values?: string) {
    this.gtmFacade
      .getProductInformation(eventItems)
      .pipe(take(1), withLatestFrom(this.store.pipe(select(getCurrentBasket))))
      .subscribe(([item, basket]) => {
        let isReviewEvent: boolean;
        if (event === 'review_checkout') {
          isReviewEvent = true;
        }
        const ecommerce: DataLayerObject = {
          ecommerce: {
            //@ts-ignore
            currency: this.currentCurrency,
            items: item,
            value: GTMHelper.getFormattedPrice(GTMHelper.calculatePriceValue(item)),
            payment_type:
              event === 'add_payment_info' && values
                ? values
                : event === 'add_payment_info' || isReviewEvent
                ? basket.payment.displayName
                : undefined,
            shipping_tier:
              event === 'add_shipping_info' && values
                ? values
                : event === 'add_shipping_info' || isReviewEvent
                ? basket.commonShippingMethod.name
                : undefined,
          },
        };
        this.gtmFacade.pushingEmptyEventToGTM(event);
        this.gtmFacade.triggeringGTMEvent(event, {
          event,
          value: { ecommerce: GTMHelper.filterUndefinedValues(ecommerce.ecommerce) },
        });
      });
  }
  //Search
  triggeringSearchEvent(event: string, searchTerm?: DataLayerObject) {
    this.gtmFacade.pushingEmptyEventToGTM(event);
    this.gtmFacade.triggeringGTMEvent(event, {
      event,
      value: searchTerm,
    });
  }

  //Checkout
  //Begin checkout,login
  triggeringEventOfUserIdEvent(event: string) {
    if (event === 'begin_checkout') {
      if (JSON?.parse(this.cookiesService.get('apiToken')).isAnonymous) {
        this.gtmFacade.getItemsForBeginCheckoutGTMEvent$().subscribe(items => {
          this.gtmFacade.pushingEmptyEventToGTM(event);
          this.gtmFacade.triggeringGTMEvent(event, {
            event,
            value: {
              ecommerce: {
                items,
                currency: this.currentCurrency,
                value: GTMHelper.getFormattedPrice(GTMHelper.calculatePriceValue(items)),
              },
              user_id: 'anonymous_user',
            },
          });
        });
      } else {
        this.getUserInfoEmitAppropriateEvent(event);
      }
    } else {
      this.getUserInfoEmitAppropriateEvent(event);
    }
  }

  triggeringEventOfSignUpEvent(event: string) {
    this.gtmFacade.pushingEmptyEventToGTM(event);
    this.gtmFacade.triggeringGTMEvent(event, {
      event,
    });
  }
  //Shipping Info
  triggeringShippingMethodEvent(shippingMethod: string) {
    this.gtmFacade.triggeringGTMEvent('add_shipping_info', {
      value: { ecommerce: { shipping_tier: shippingMethod } },
    });
  }
  //Payment Event
  triggeringPaymentInfoEvent(payment: Payment) {
    this.gtmFacade.triggeringGTMEvent('add_payment_info', {
      value: { ecommerce: { payment_type: payment?.displayName } },
    });
  }
  //Purchase
  triggeringPurchaseGTMEvent(order: Order) {
    this.gtmFacade.getProductInformation(order.lineItems).subscribe(items => {
      const eventObject = {
        items,
        tax: order?.totals?.taxTotal?.value,
        shipping: order?.commonShippingMethod?.name,
        value: GTMHelper.getFormattedPrice(order?.totals?.total?.gross),
        currency: order.purchaseCurrency,
        transaction_id: order?.id,
      };
      this.gtmFacade.pushingEmptyEventToGTM('purchase');
      this.gtmFacade.triggeringGTMEvent('purchase', { value: { ecommerce: eventObject, user_id: order.email } });
    });
  }

  triggeringDotCmsPromotionGTMEvent(event: string, items?: GTMEventItems[]) {
    this.gtmFacade.triggeringContentletEvent(event, items);
    const link = items[0]?.link;
    if (link) {
      if (link.startsWith('http://') || link.startsWith('https://')) {
        window.location.href = link;
      } else {
        this.router.navigate([link]);
      }
    }
  }

  getBasketItemDetails(lineItemId: string): LineItem[] {
    const lineItems: LineItem[] = [];
    this.store.pipe(select(getBasketItem(lineItemId))).subscribe(lineItem => {
      lineItems.push(lineItem);
    });
    return lineItems;
  }

  private getUserInfoEmitAppropriateEvent(event: string) {
    this.store
      .pipe(
        select(getLoggedInUser),
        filter(user => !!user),
        take(1),
        switchMap(user => {
          console.log('user data ', user);
          if (event === 'begin_checkout') {
            return combineLatest([of(user), this.gtmFacade.getItemsForBeginCheckoutGTMEvent$()]);
          } else {
            return combineLatest([of(user), of([])]);
          }
        })
      )
      .subscribe(([user, basket]) => {
        this.gtmFacade.pushingEmptyEventToGTM(event);
        this.gtmFacade.triggeringGTMEvent(event, {
          event,
          value: GTMHelper.filterUndefinedValues({
            user_id: `${user?.firstName} ${user?.lastName}`,
            user_email: user.email,
            ecommerce: {
              items: basket.length > 0 ? basket : undefined,
              currency: basket.length > 0 ? this.currentCurrency : undefined,
              value: basket.length > 0 ? GTMHelper.getFormattedPrice(GTMHelper.calculatePriceValue(basket)) : undefined,
            },
          }),
        });
      });
  }
}
